<template>
    <DeadlineReminderCard
        v-if="notification.type === 'Deadline Reminder'"
        :notification="notification"
        @open="redirectToLink(notification)"
        @toggle-read="toggleReadStatus(notification, !notification.read)"
    />
    <div
        v-else
        class="tw:group tw:relative tw:flex tw:items-center tw:border-b tw:border-surface tw:bg-surface-0 tw:hover:bg-emphasis tw:focus-within:bg-emphasis tw:dark:bg-surface-900"
        data-test="alert-card"
    >
        <span
            v-if="!notification.read"
            class="tw:absolute tw:top-[1.6rem] tw:left-1.5 tw:size-1.5 tw:rounded-full tw:bg-primary"
            aria-hidden="true"
            data-test="unread-dot"
        />
        <button
            type="button"
            class="tw:flex tw:min-w-0 tw:flex-1 tw:cursor-pointer tw:items-start tw:gap-3 tw:border-0 tw:bg-transparent tw:py-3.5 tw:pr-2 tw:pl-5 tw:text-left tw:focus-visible:outline-2 tw:focus-visible:-outline-offset-2 tw:focus-visible:outline-primary"
            data-test="alert-card-open"
            @click="redirectToLink(notification)"
        >
            <Avatar
                :image="creatorPhoto"
                :label="creatorPhoto ? undefined : creatorInitials"
                :icon="creatorPhoto || creatorInitials ? undefined : 'pi pi-user'"
                size="large"
                shape="circle"
                class="tw:shrink-0"
                aria-hidden="true"
            />
            <span class="tw:block tw:min-w-0 tw:flex-1">
                <span v-if="!notification.read" class="tw:sr-only">Unread:</span>
                <span
                    :class="[
                        'tw:line-clamp-2 tw:text-[0.9rem] tw:leading-5 tw:break-words',
                        notification.read ? 'tw:font-normal tw:text-muted-color' : 'tw:font-bold tw:text-color',
                    ]"
                    data-test="alert-card-title"
                >
                    {{ title }}
                </span>
                <span
                    :class="[
                        'tw:mt-0.5 tw:line-clamp-2 tw:text-sm tw:leading-5 tw:break-words',
                        notification.read ? 'tw:text-muted-color' : 'tw:text-color',
                    ]"
                    data-test="alert-card-body"
                >
                    {{ body }}
                </span>
                <span class="tw:mt-1 tw:block tw:text-xs tw:leading-[1.125rem] tw:text-muted-color" data-test="alert-card-meta">
                    {{ meta }}
                </span>
            </span>
        </button>
        <PrimeVueButton
            :icon="notification.read ? 'pi pi-circle' : 'pi pi-check'"
            :aria-label="notification.read ? 'Mark as unread' : 'Mark as read'"
            class="tw:mr-3 tw:shrink-0 tw:opacity-0 tw:group-hover:opacity-100 tw:group-focus-within:opacity-100 tw:pointer-coarse:opacity-100"
            severity="secondary"
            size="small"
            text
            rounded
            data-test="alert-card-toggle-read"
            @click.stop="toggleReadStatus(notification, !notification.read)"
        />
    </div>
</template>

<script>
import Avatar from 'primevue/avatar';
import PrimeVueButton from 'primevue/button';

import DeadlineReminderCard from '@/components/alerts/DeadlineReminderCard.vue';
import { trackNotificationBellItemOpened } from '@/components/alerts/notificationBellAnalytics.js';
import { snippet } from '@/components/inbox/inboxRows.js';
import { handleResponseError } from '@/Helpers.js';
import BellNotification from '@/models/BellNotification.js';

/**
 * Short labels for the meta line; unknown types fall back to the raw type.
 */
const typeLabels = {
    'Activity Update': 'Activity update',
    'Course Announcement': 'Announcement',
    'Discussion Reply': 'Reply',
    Message: 'Message',
};

export default {
    name: 'AlertCard',
    components: { Avatar, DeadlineReminderCard, PrimeVueButton },
    emits: ['update-notification'],

    props: {
        notification: {
            type: Object,
            required: true,
        },
        isExpanded: {
            type: Boolean,
            default: false,
        },
    },

    computed: {
        title() {
            return this.notification.title || 'Untitled';
        },
        body() {
            return snippet(this.notification.body) || 'No message.';
        },
        meta() {
            const type = typeLabels[this.notification.type] ?? this.notification.type;
            // Messages have no course; the payload puts their type in the course-code slot.
            const courseCode = this.notification.course_code === type ? null : this.notification.course_code;

            return [courseCode, type, this.notification.relative_created_at ?? this.notification.formatted_created_at]
                .filter(Boolean)
                .join(' · ');
        },
        creatorPhoto() {
            return this.notification.creator?.photo_url || undefined;
        },
        creatorInitials() {
            const { creator } = this.notification;
            const words = [creator?.first_name, creator?.last_name].every(Boolean)
                ? [creator.first_name, creator.last_name]
                : (creator?.name ?? '').trim().split(/\s+/).filter(Boolean);
            const letters = words.length > 1 ? [words[0], words.at(-1)] : words;

            return letters.map(word => word.charAt(0).toUpperCase()).join('') || undefined;
        },
    },

    methods: {
        async toggleReadStatus(notification, read) {
            if (notification.type === 'Deadline Reminder') {
                const pending = read ? notification.markAsRead() : notification.markAsUnread();
                this.$emit('update-notification', notification, { immediate: true, pending });
                try {
                    await pending;
                    await BellNotification.fetch();
                } catch (error) {
                    handleResponseError(error);
                }
                return;
            }

            await BellNotification.update({
                where: notification.notification_id,
                data: { read },
            });
            this.$emit('update-notification', notification);
        },

        async redirectToLink(notification) {
            if (!notification.inbox_link) {
                return;
            }

            trackNotificationBellItemOpened(notification);

            // Mark as read before navigating so the unread count is correct when
            // the destination page loads and re-fetches it. We await the request
            // (rather than fire-and-forget) because the full-page navigation would
            // otherwise cancel the in-flight PUT. Navigate regardless of outcome.
            try {
                await notification.markAsRead();
            } catch {
                // Swallow — navigation should still proceed on a failed read.
            } finally {
                window.location.href = notification.inbox_link;
            }
        },
    },
};
</script>
